import { AlertManager, useAlerts } from "client/Hooks";
import { delay } from "client/Misc/utils";
import {
	checkIsDmsInitialized,
	DocumentStoreInitializationState,
	IDocumentStoreInitializationModel
} from "client/Api/Documents/documents";
import { DocumentStoreType } from "client/Api/Documents/common";
import { useEffect, useState } from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";

export function useInitializer(
	models: readonly IDocumentStoreInitializationModel[],
	shouldIgnoreCurrentNavigation: boolean
): readonly IDocumentStoreInitializationModel[] | undefined {

	const [fullyInitializedModels, setFullyInitializedModels] = useState<readonly IDocumentStoreInitializationModel[] | undefined>();

	const alertManager: AlertManager = useAlerts();

	useEffect(
		(): (() => void) => {
			let storeInitializationModels: readonly IDocumentStoreInitializationModel[] = models;

			// we need to pass the same parameters to check action as in Index
			// otherwise it may be caught in redirect loop
			const urlQueryString: string | undefined = shouldIgnoreCurrentNavigation
				? undefined
				: window.location.search;

			const fullyInitializeStores = async (signal: AbortSignal): Promise<void> => {
				setFullyInitializedModels(undefined);

				if (isAnyStoreStillInProgress(storeInitializationModels)) {
					try {
						while (true) {
							storeInitializationModels = await checkIsDmsInitialized(urlQueryString, signal);

							if (isAnyStoreStillInProgress(storeInitializationModels)) {
								await delay(1000, signal);
							} else {
								break;
							}
						}

					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						alertManager.showError(localize("_GeneralErrorMessage"));
						return;
					}
				}

				setFullyInitializedModels(storeInitializationModels);
			};

			const cancellation = new AbortController();
			fullyInitializeStores(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			models,
			shouldIgnoreCurrentNavigation
		]
	);

	return fullyInitializedModels;
}


export function isAnyStoreStillInProgress(models: readonly IDocumentStoreInitializationModel[]): boolean {
	return models.some(x => x.state === DocumentStoreInitializationState.InProgress);
}

export function areAllStoresNotConfigured(models: readonly IDocumentStoreInitializationModel[]): boolean {
	return models.every((x) => x.state === DocumentStoreInitializationState.NotConfigured);
}

export function didAllConfiguredStoresFailed(models: readonly IDocumentStoreInitializationModel[]): boolean {
	return models.every((x) =>
		x.state === DocumentStoreInitializationState.NotConfigured
		|| x.state === DocumentStoreInitializationState.Failed
	);
}

export function getConfiguredStoreTypes(models: readonly IDocumentStoreInitializationModel[]): readonly DocumentStoreType[] {
	return models.filter((x) => x.state !== DocumentStoreInitializationState.NotConfigured)
		.map((x) => x.storeType);
}